import {
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow
} from "@material-ui/core";
import { CloseOutlined } from "@material-ui/icons";
import DecimalFormat from "@saleor/components/DecimalFormat";
import MyTypography from "@saleor/components/MyTypography";
import TableCellHeader from "@saleor/components/TableCellHeader";
import { GetCreditLimitQuery } from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import React, { useMemo } from "react";
import { useIntl } from "react-intl";

interface RouterProps {
  open: boolean;
  onClose: () => void;
  dataModal: GetCreditLimitQuery["getCreditLimit"];
  soldTo: string;
  title?: string;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      "& .MuiPaper-root": {
        maxWidth: 480,
        boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.25)",
        borderRadius: 16,
        padding: 24,
        overflow: "auto"
      },
      "& .MuiTableCell-root": {
        height: 40
      },
      "& .MuiTableCell-body": {
        paddingTop: 0,
        paddingBottom: 0
      },
      "& .MuiDialogActions-root": {
        borderTop: 0,
        padding: "24px 0 0 0",
        gap: 24
      }
    },
    dialogTitle: {
      padding: 0,
      "& .MuiTypography-root": {
        display: "flex",
        alignItems: "center"
      }
    },
    mainTitle: {
      color: theme.palette.info.main
    },
    closeIcon: {
      marginLeft: "auto",
      cursor: "pointer"
    },
    dialogCenter: {
      padding: "0px !important",
      marginTop: "24px !important",
      "& .MuiFormControl-root": {
        height: 40
      }
    },
    buttonAction: {
      width: "50%",
      height: 48,
      "& svg": {
        marginRight: 10
      }
    },
    nameField: {
      marginBottom: 10
    },
    boxTable: {
      marginTop: "2rem",
      border: `solid 1px ${theme.palette.grey[300]}`
    },
    cellTextInfo: {
      color: theme.palette.info.dark
    },
    cellCol1: {
      paddingLeft: "1rem !important"
    }
  }),
  { name: "ModalResultRoute" }
);

const ModalResultRouter: React.FC<RouterProps> = props => {
  const { open, onClose, dataModal, soldTo, title } = props;
  const classes = useStyles();
  const intl = useIntl();

  // TODO: resolve creditAvailable subtract prob
  const arrTable = useMemo(
    () => [
      {
        col1: "Credit Control Area",
        col2: dataModal?.creditControlArea
      },
      { col1: "Currency", col2: dataModal?.currency },
      {
        col1: "วงเงินสินเชื่อ",
        col2: <DecimalFormat value={Number(dataModal?.creditLimit) || 0} />
      },
      {
        col1: "วงเงินใช้ไป",
        col2: <DecimalFormat value={Number(dataModal?.creditExposure) || 0} />
      },
      { col1: "วงเงินใช้ไป (%)", col2: `${dataModal?.creditLimitUsed || 0}%` },
      {
        col1: "วงเงินคงเหลือที่ใช้ได้",
        col2: (
          <DecimalFormat
            value={
              Number(dataModal?.creditLimit) -
                Number(dataModal?.creditExposure) || 0
            }
          />
        )
      },
      {
        col1: "Billing ที่รอชำระเงิน",
        col2: <DecimalFormat value={Number(dataModal?.receivables) || 0} />
      },
      {
        col1: "รับเช็คล่วงหน้า",
        col2: <DecimalFormat value={Number(dataModal?.specialLiabil) || 0} />
      },
      {
        col1: "Sale Value",
        col2: <DecimalFormat value={Number(dataModal?.saleValue) || 0} />
      },
      {
        col1: "Second Receivable",
        col2: (
          <DecimalFormat value={Number(dataModal?.secondReceivables) || 0} />
        )
      },
      {
        col1: "เครดิต Block",
        col2: Boolean(dataModal?.creditBlockStatus) ? "True" : "False"
      }
    ],
    [dataModal]
  );

  return (
    <Dialog
      data-testid="modalCreditMaster"
      className={classes.root}
      open={open}
      onClose={onClose}
      fullWidth
    >
      <DialogTitle className={classes.dialogTitle}>
        <MyTypography variant={"h6"} className={classes.mainTitle}>
          {title || intl.formatMessage({ defaultMessage: "Input Parameter" })}
        </MyTypography>
        <CloseOutlined
          data-testid="closeButton"
          className={classes.closeIcon}
          onClick={onClose}
        />
      </DialogTitle>
      <DialogContent className={classes.dialogCenter}>
        <MyTypography variant={"subtitle3"}>Sold-to Party</MyTypography>
        <MyTypography variant={"body3"}>{soldTo}</MyTypography>
        <div className={classes.boxTable}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCellHeader colSpan={2} className={classes.cellCol1}>
                  <MyTypography variant={"captionBold"}>
                    {intl.formatMessage({ defaultMessage: "รายละเอียด" })}
                  </MyTypography>
                </TableCellHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {arrTable.map(item => (
                <TableRow key={item.col1}>
                  <TableCell className={classes.cellCol1}>
                    <MyTypography
                      variant={"body3"}
                      className={classes.cellTextInfo}
                    >
                      {item.col1}
                    </MyTypography>
                  </TableCell>
                  <TableCell>
                    <MyTypography variant={"body3"}>{item.col2}</MyTypography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ModalResultRouter;
